import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Lbl } from './ApplicantInterviews';
import { useHiringApi, errMsg } from './useHiringApi';
import { fullName } from './hiringConstants';

export default function ConvertToGuruDialog({ a, open, onOpenChange, onConverted }) {
  const api = useHiringApi();
  const [f, setF] = useState({ password: '', worker_type: a.offer?.arrangement?.startsWith('1099') ? '1099' : 'W2', pay_structure: 'hourly', hourly_rate: '', display_name: a.first_name || '', onboarding_ready_confirmed: false });
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error('Temporary password must be at least 8 characters'); return; }
    setSaving(true); setConflict(null);
    try {
      const r = await api.convert(a.id, { ...f, hourly_rate: f.hourly_rate === '' ? null : Number(f.hourly_rate) });
      toast.success('Guru account created'); onConverted(r);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code) setConflict(d); else toast.error(errMsg(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="convert-dialog">
        <DialogHeader><DialogTitle>Create Guru account for {fullName(a)}</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500 -mt-2">Uses the existing Guru creation flow. The recruiting record stays intact and is linked to the new account. This never happens automatically.</p>
        {conflict && (
          <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-2" data-testid="convert-conflict">
            {conflict.code === 'already_converted' ? 'A Guru account was already created from this applicant.' : conflict.message}
          </p>
        )}
        <form onSubmit={submit} className="grid grid-cols-2 gap-2">
          <div className="col-span-2"><Lbl text={`Login email: ${a.email}`}><span /></Lbl></div>
          <div className="col-span-2"><Lbl text="Temporary password *"><Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="convert-password" /></Lbl></div>
          <Lbl text="Worker type"><Select value={f.worker_type} onValueChange={(v) => setF({ ...f, worker_type: v })}><SelectTrigger data-testid="convert-worker-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="W2">W-2 employee</SelectItem><SelectItem value="1099">1099 contractor</SelectItem></SelectContent></Select></Lbl>
          <Lbl text="Pay structure"><Select value={f.pay_structure} onValueChange={(v) => setF({ ...f, pay_structure: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="commission">Commission</SelectItem></SelectContent></Select></Lbl>
          <Lbl text="Hourly rate ($)"><Input type="number" step="0.01" value={f.hourly_rate} onChange={(e) => setF({ ...f, hourly_rate: e.target.value })} /></Lbl>
          <Lbl text="Display name"><Input value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} /></Lbl>
          <label className="col-span-2 flex items-start gap-2 text-xs text-zinc-700">
            <input type="checkbox" checked={f.onboarding_ready_confirmed} onChange={(e) => setF({ ...f, onboarding_ready_confirmed: e.target.checked })} data-testid="convert-confirm" />
            <span>I confirm this person has been hired and onboarding readiness has been confirmed.</span>
          </label>
          <div className="col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !f.onboarding_ready_confirmed} className="bg-green-700 hover:bg-green-800" data-testid="convert-submit">{saving ? 'Creating…' : 'Create Guru account'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
